import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { Brand } from './Brand.tsx';
import { UserMenu } from './UserMenu.tsx';
import s from './shell.module.css';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /** Aktiivinen vain täsmälleen tällä polulla (etusivu). */
  end?: boolean;
  /** Lukumäärä kohdan perässä, esim. lukemattomat viestit. */
  badge?: number;
}

interface SidebarProps {
  items: NavItem[];
  home: string;
  top?: ReactNode;
}

/**
 * Sivupaneeli. Aktiivinen kohta on kohotettu laatta, joka liukuu kohdasta toiseen
 * navigoitaessa. Puhelimessa paneeli muuttuu alapalkiksi (shell.module.css).
 */
export function Sidebar({ items, home, top }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const listRef = useRef<HTMLUListElement>(null);
  const [pill, setPill] = useState<{ y: number; h: number } | null>(null);

  useLayoutEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>('a[aria-current="page"]');
    setPill(active ? { y: active.offsetTop, h: active.offsetHeight } : null);
  }, [location.pathname, items]);

  return (
    <aside className={s.sidebar}>
      <Brand to={home} />
      {top && <div className={s.sidebarTop}>{top}</div>}
      <nav className={s.nav} aria-label={t('nav.main')}>
        <ul className={s.navList} ref={listRef}>
          <li
            className={s.pill}
            aria-hidden="true"
            style={{
              transform: `translateY(${pill?.y ?? 0}px)`,
              height: pill?.h ?? 44,
              opacity: pill ? 1 : 0,
            }}
          />
          {items.map(({ to, labelKey, icon: Icon, end, badge }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) => `${s.navItem} ${isActive ? s.navItemActive : ''}`}
              >
                <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
                <span>{t(labelKey)}</span>
                {badge ? (
                  <span className={s.navBadge}>
                    {badge}
                    <span className="visually-hidden"> {t('nav.unread')}</span>
                  </span>
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className={s.sidebarFooter}>
        <UserMenu placement="up" />
      </div>
    </aside>
  );
}
